import React, { useState, useEffect, useRef } from 'react';
import { getDatesInRange, downloadAllEventsIcs, parseTimeToMinutes } from '../../utils/helpers';
import BlogPage from '../blog/BlogPage';
import CarouselBlock from '../ui/CarouselBlock';
import SummaryBlock from '../ui/SummaryBlock';

// Importez tous vos panneaux de propriétés
import TextProperties from './properties/TextProperties';
import LogoProperties from './properties/LogoProperties';
import ImageProperties from './properties/ImageProperties';
import CarouselProperties from './properties/CarouselProperties';
import HtmlProperties from './properties/HtmlProperties';
import AnchorProperties from './properties/AnchorProperties';
import SummaryProperties from './properties/SummaryProperties';
import AgendaProperties from './properties/AgendaProperties';
import BlogTitleProperties from './properties/BlogTitleProperties';

const DesignEditor = ({ seminar, onSave }) => {
    const [blocks, setBlocks] = useState(seminar.design || []);
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const previewRef = useRef(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [agendaFilter, setAgendaFilter] = useState('all');
    const [selectedAgendaDate, setSelectedAgendaDate] = useState(seminar.startDate);
    const [isFullCalendarMenuOpen, setIsFullCalendarMenuOpen] = useState(false);
    const calendarMenuRef = useRef(null);
    const [mobileView, setMobileView] = useState('home');

    const seminarDates = seminar.isMultiDay && seminar.startDate && seminar.endDate ? getDatesInRange(seminar.startDate, seminar.endDate) : [seminar.startDate];

    useEffect(() => {
        if (!selectedAgendaDate && seminar.startDate) {
            setSelectedAgendaDate(seminar.startDate);
        }
    }, [seminar.startDate, selectedAgendaDate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarMenuRef.current && !calendarMenuRef.current.contains(event.target)) {
                setIsFullCalendarMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [calendarMenuRef]);

    useEffect(() => {
        const previewEl = previewRef.current;
        if (!previewEl) return;

        const handleScroll = () => {
            if (previewEl.scrollTop > 100) {
                setShowScrollButton(true);
            } else {
                setShowScrollButton(false);
            }
        };

        previewEl.addEventListener('scroll', handleScroll);
        return () => {
            previewEl.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleScrollToTop = (e) => {
        e.stopPropagation();
        previewRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const addBlock = (type) => {
        const newBlock = {
            id: `block-${Date.now()}`,
            type,
            content: type === 'title' ? (seminar.name || 'Titre') : 'Paragraphe de texte.',
            backgroundColor: '#FFFFFF',
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 16,
            paddingRight: 16,
            textAlign: 'left',
            fontSize: type === 'title' ? 24 : 16,
            fontWeight: type === 'title' ? 'bold' : 'normal',
            fontFamily: 'Lato',
            fontStyle: 'normal',
            textDecoration: 'none',
            color: '#000000',
            isFullWidth: false,
        };
        if (type === 'image') newBlock.content = '';
        if (type === 'logo') {
            newBlock.content = seminar.logoUrl || 'https://placehold.co/100x50?text=Logo';
        }
        if (type === 'agenda') {
            const titleBlock = { ...newBlock, id: `block-${Date.now()}`, type: 'title', content: 'Agenda', isAnchor: true };
            const agendaBlock = { ...newBlock, id: `block-${Date.now()+1}`, type: 'agenda', content: [], titleFontSize: 14, titleFontWeight: 'bold', titleColor: '#000000', roomFontSize: 12, roomFontWeight: 'normal', roomColor: '#555555', descriptionFontSize: 12, descriptionFontWeight: 'normal', descriptionColor: '#333333' };
            const newBlocks = [...blocks, titleBlock, agendaBlock];
            setBlocks(newBlocks);
            setSelectedBlockId(agendaBlock.id);
            onSave({ ...seminar, design: newBlocks });
            return;
        }
        if (type === 'carousel') newBlock.content = [];
        if (type === 'anchor') {
            newBlock.content = 'Nom de l\'ancre';
            newBlock.isAnchor = true;
        }
        if (type === 'summary') newBlock.content = [];

        const newBlocks = [...blocks, newBlock];
        setBlocks(newBlocks);
        setSelectedBlockId(newBlock.id);
        onSave({ ...seminar, design: newBlocks });
    };
    
    const updateBlock = (id, newContent) => {
        const newBlocks = blocks.map(b => b.id === id ? { ...b, ...newContent } : b);
        setBlocks(newBlocks);
        onSave({ ...seminar, design: newBlocks });
    };

    const deleteBlock = (id) => {
        const newBlocks = blocks.filter(b => b.id !== id);
        setBlocks(newBlocks);
        onSave({ ...seminar, design: newBlocks });
    };

    const moveBlock = (index, direction) => {
        const newBlocks = [...blocks];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= newBlocks.length) return;
        [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
        setBlocks(newBlocks);
        onSave({ ...seminar, design: newBlocks });
    };

    const selectedBlock = blocks.find(b => b.id === selectedBlockId);

    return (
        <div className="flex h-full">
            {/* Boîte à outils */}
            <div className="w-1/4 p-4 bg-gray-100 border-r overflow-y-auto">
                {mobileView !== 'blog' ? (
                    <>
                        <h3 className="font-bold mb-4">Ajouter un bloc</h3>
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-500 text-sm mb-2">Texte</h4>
                                <button onClick={() => addBlock('title')} className="w-full p-2 bg-white border rounded text-left">Titre</button>
                                <button onClick={() => addBlock('text')} className="w-full p-2 mt-2 bg-white border rounded text-left">Paragraphe</button>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-500 text-sm mb-2">Images</h4>
                                <button onClick={() => addBlock('logo')} className="w-full p-2 bg-white border rounded text-left">Logo</button>
                                <button onClick={() => addBlock('image')} className="w-full p-2 mt-2 bg-white border rounded text-left">Image</button>
                                <button onClick={() => addBlock('carousel')} className="w-full p-2 mt-2 bg-white border rounded text-left">Carrousel</button>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-500 text-sm mb-2">Navigation</h4>
                                <button onClick={() => addBlock('summary')} className="w-full p-2 bg-white border rounded text-left">Sommaire</button>
                                <button onClick={() => addBlock('anchor')} className="w-full p-2 mt-2 bg-white border rounded text-left">Ancre</button>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-500 text-sm mb-2">Planning</h4>
                                <button onClick={() => addBlock('agenda')} className="w-full p-2 bg-white border rounded text-left">Agenda</button>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-500 text-sm mb-2">Autre</h4>
                                <button onClick={() => addBlock('html')} className="w-full p-2 bg-white border rounded text-left">Code HTML</button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center text-gray-500">
                        <p>L'édition des blocs est désactivée en mode aperçu du blog.</p>
                    </div>
                )}
            </div>

            {/* Aperçu Smartphone */}
            <div className="flex-1 flex justify-center items-center p-4 bg-gray-200">
                <div className="w-full max-w-[375px] aspect-[375/667] bg-white shadow-lg rounded-3xl border-8 border-black relative overflow-hidden">
                    <div ref={previewRef} className={`w-full h-full overflow-y-auto ${seminar.hasBlog ? 'pb-16' : ''}`}>
                       {mobileView === 'home' ? (
                            <>
                                {blocks.map((block, index) => (
                                    <div 
                                        key={block.id} 
                                        id={block.isAnchor ? block.id : null}
                                        style={{ 
                                            backgroundColor: block.backgroundColor,
                                            paddingTop: `${block.isFullWidth ? 0 : block.paddingTop || 0}px`,
                                            paddingBottom: `${block.isFullWidth ? 0 : block.paddingBottom || 0}px`,
                                            paddingLeft: `${block.isFullWidth ? 0 : block.paddingLeft || 0}px`,
                                            paddingRight: `${block.isFullWidth ? 0 : block.paddingRight || 0}px`,
                                        }} 
                                        className={`border-b relative group ${selectedBlockId === block.id ? 'ring-2 ring-blue-500' : ''}`}
                                        onClick={() => setSelectedBlockId(block.id)}
                                    >
                                        <div className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex bg-white rounded shadow">
                                            <button onClick={(e) => { e.stopPropagation(); moveBlock(index, 'up'); }} className="p-1 hover:bg-gray-200" disabled={index === 0}>▲</button>
                                            <button onClick={(e) => { e.stopPropagation(); moveBlock(index, 'down'); }} className="p-1 hover:bg-gray-200" disabled={index === blocks.length - 1}>▼</button>
                                            <button onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }} className="p-1 text-red-500 hover:bg-gray-200">X</button>
                                        </div>
                        
                                        {block.type === 'title' && <h1 style={{ textAlign: block.textAlign, fontSize: `${block.fontSize}px`, fontWeight: block.fontWeight, fontFamily: block.fontFamily, fontStyle: block.fontStyle, textDecoration: block.textDecoration, color: block.color }}>{block.content}</h1>}
                                        {block.type === 'text' && <div dangerouslySetInnerHTML={{ __html: block.content }} style={{ textAlign: block.textAlign, fontSize: `${block.fontSize}px`, fontWeight: block.fontWeight, fontFamily: block.fontFamily, fontStyle: block.fontStyle, textDecoration: block.textDecoration, color: block.color }} />}
                                        {block.type === 'logo' && (
                                            <div style={{ textAlign: 'center' }}>
                                                <img 
                                                    src={block.content} 
                                                    alt="Logo du séminaire" 
                                                    style={{ 
                                                        maxWidth: '33.33%', 
                                                        display: 'inline-block', 
                                                        height: 'auto' 
                                                    }} 
                                                />
                                            </div>
                                        )}
                                        {block.type === 'image' && <img src={block.content || 'https://placehold.co/375x200?text=Image'} alt="placeholder" className="w-full" />}
                                        {block.type === 'carousel' && <CarouselBlock block={block} />}
                                        {block.type === 'html' && <div dangerouslySetInnerHTML={{ __html: block.content }} />}
                                        {block.type === 'anchor' && (
                                            <div className="p-2 my-2 bg-gray-200 border-l-4 border-gray-400 text-gray-600 text-sm italic flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9.243 3.03a1 1 0 01.727 1.213L9.53 6h1.94l.56-2.243a1 1 0 111.94.486L13.53 6H16a1 1 0 110 2h-2.47l-.56 2.242a1 1 0 11-1.94-.485L11.47 8H9.53l-.56 2.242a1 1 0 11-1.94-.485L7.47 8H4a1 1 0 110-2h2.47l.56-2.243a1 1 0 011.213-.727zM9.53 10h1.94l.56 2.242a1 1 0 11-1.94.485L9.53 10z" clipRule="evenodd" />
                                                </svg>
                                                Ancre: {block.content}
                                            </div>
                                        )}
                                        {block.type === 'summary' && <SummaryBlock id={`summary-${block.id}`} block={block} allBlocks={blocks} />}
                                        {block.type === 'agenda' && (
                                            <>
                                                <div className="p-2 bg-gray-100 text-center border-b relative">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setIsFullCalendarMenuOpen(prev => !prev);
                                                        }}
                                                        className="w-full text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center justify-center gap-2"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                        </svg>
                                                        Ajouter au calendrier
                                                    </button>
                                                    {isFullCalendarMenuOpen && (
                                                        <div ref={calendarMenuRef} className="absolute z-20 top-full mt-2 w-48 bg-white rounded-md shadow-lg border right-1/2 translate-x-1/2">
                                                            <button onClick={() => downloadAllEventsIcs(block.content, seminar.name)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Google Calendar</button>
                                                            <button onClick={() => downloadAllEventsIcs(block.content, seminar.name)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Apple Calendar</button>
                                                            <button onClick={() => downloadAllEventsIcs(block.content, seminar.name)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Outlook</button>
                                                        </div>
                                                    )}
                                                </div>
                                                {seminarDates.length > 1 && (
                                                    <div className="p-2 flex flex-wrap gap-2 bg-gray-100 sticky top-0 z-10">
                                                        {seminarDates.map(date => (
                                                            <button key={date} onClick={(e) => {e.stopPropagation(); setSelectedAgendaDate(date)}} className={`px-2 py-1 text-xs rounded-full ${selectedAgendaDate === date ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>{new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}</button>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="p-2 flex flex-wrap gap-2 bg-gray-100 sticky top-10 z-10">
                                                    <button 
                                                        onClick={(e) => {e.stopPropagation(); setAgendaFilter('all')}} 
                                                        className={`px-2 py-1 text-xs rounded-full text-white`}
                                                        style={{ backgroundColor: '#22D6B5', opacity: agendaFilter === 'all' ? 1 : 0.6 }}
                                                    >
                                                        Tous
                                                    </button>
                                                    {(seminar.groups || []).map(group => (
                                                        <button 
                                                            key={group.id} 
                                                            onClick={(e) => {e.stopPropagation(); setAgendaFilter(group.id)}} 
                                                            className={`px-2 py-1 text-xs rounded-full text-white`}
                                                            style={{ backgroundColor: group.color || '#cccccc', opacity: agendaFilter === group.id ? 1 : 0.6 }}
                                                        >
                                                            {group.name}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="p-4">
                                                    {(() => {
                                                        const filteredEvents = block.content.filter(event =>
                                                            event.date === selectedAgendaDate &&
                                                            (agendaFilter === 'all' ||
                                                            !event.assignedGroupIds ||
                                                                event.assignedGroupIds.length === 0 ||
                                                                event.assignedGroupIds.includes(agendaFilter))
                                                        );
                                        
                                                        if (filteredEvents.length === 0) {
                                                            return <p className="text-center text-gray-500 py-4">Aucun événement pour ce filtre.</p>;
                                                        }
                                        
                                                        const eventsByTime = filteredEvents.reduce((acc, event) => {
                                                            (acc[event.time] = acc[event.time] || []).push(event);
                                                            return acc;
                                                        }, {});
                                        
                                                        return Object.entries(eventsByTime)
                                                            .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
                                                            .map(([time, events]) => (
                                                                <div key={time} className="flex mb-4 items-start">
                                                                    <div className="w-1/4 font-bold text-sm text-gray-600 pr-2 text-right">
                                                                        <p>{time}</p>
                                                                    </div>
                                                                    <div className="w-3/4 flex flex-col gap-2">
                                                                        {events.map(event => {
                                                                            const startTimeMinutes = parseTimeToMinutes(event.time);
                                                                            const endTimeMinutes = parseTimeToMinutes(event.endTime);
                                                                            const duration = Math.max(0, endTimeMinutes - startTimeMinutes);
                                                                            const minHeight = 40;
                                                                            const pixelsPerMinute = 1.2;
                                                                            const height = Math.max(minHeight, duration * pixelsPerMinute);
                                                        
                                                                            return (
                                                                                <div key={event.id} className="w-full p-2 rounded-lg bg-gray-50 flex flex-col relative" style={{ 
                                                                                    borderLeft: `4px solid ${event.eventColor || '#22D6B5'}`,
                                                                                    minHeight: `${height}px`
                                                                                }}>
                                                                                    <p style={{fontSize: `${block.titleFontSize || 14}px`, fontWeight: block.titleFontWeight || 'bold', color: block.titleColor || '#000000'}}>{event.title}</p>
                                                                                    <p className="text-xs text-gray-500">{event.time} - {event.endTime}</p>
                                                                                    {event.room && <p style={{fontSize: `${block.roomFontSize || 12}px`, fontWeight: block.roomFontWeight || 'normal', color: block.roomColor || '#555555'}} className="mt-1">{event.room}</p>}
                                                                                    {height > 70 && event.description && <p style={{fontSize: `${block.descriptionFontSize || 12}px`, fontWeight: block.descriptionFontWeight || 'normal', color: block.descriptionColor || '#333333'}} className="mt-1 overflow-hidden">{event.description}</p>}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            ));
                                                    })()}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </>
                       ) : (
                            <BlogPage 
                                seminar={seminar} 
                                onSave={onSave} 
                                onSelectTitle={() => setSelectedBlockId('blog-title')}
                            />
                       )}
                    </div>

                    {showScrollButton && mobileView === 'home' && (
                        <button
                            onClick={handleScrollToTop}
                            className={`absolute ${seminar.hasBlog ? 'bottom-20' : 'bottom-4'} right-4 bg-black bg-opacity-50 text-white p-2 rounded-full z-20 transition-all`}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
                        </button>
                    )}
                    
                    {seminar.hasBlog && (
                        <div className="w-full bg-white border-t border-gray-200 p-2 flex justify-around items-center absolute bottom-0 left-0 z-10 h-16">
                             <a href="#" onClick={(e) => {e.preventDefault(); setMobileView('home')}} className={`flex flex-col items-center font-bold ${mobileView === 'home' ? 'text-[#22D6B5]' : 'text-gray-500 hover:text-[#22D6B5]'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                                <span className="text-xs">Accueil</span>
                            </a>
                             <a href="#" onClick={(e) => {e.preventDefault(); setMobileView('blog')}} className={`flex flex-col items-center font-bold ${mobileView === 'blog' ? 'text-[#22D6B5]' : 'text-gray-500 hover:text-[#22D6B5]'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3h2m0 0h2" /></svg>
                                <span className="text-xs">Blog</span>
                            </a>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Panneau de propriétés */}
            <div className="w-1/4 p-4 bg-gray-100 border-l overflow-y-auto">
                <h3 className="font-bold mb-4">Propriétés</h3>
                {selectedBlockId === 'blog-title' ? (
                    <BlogTitleProperties seminar={seminar} onSave={onSave} />
                ) : selectedBlock ? (
                    <div className="space-y-4">
                        {(selectedBlock.type === 'title' || selectedBlock.type === 'text') && <TextProperties block={selectedBlock} onUpdate={updateBlock} />}
                        {selectedBlock.type === 'logo' && <LogoProperties block={selectedBlock} onUpdate={updateBlock} />}
                        {selectedBlock.type === 'image' && <ImageProperties block={selectedBlock} onUpdate={updateBlock} />}
                        {selectedBlock.type === 'carousel' && <CarouselProperties block={selectedBlock} onUpdate={updateBlock} />}
                        {selectedBlock.type === 'html' && <HtmlProperties block={selectedBlock} onUpdate={updateBlock} />}
                        {selectedBlock.type === 'anchor' && <AnchorProperties block={selectedBlock} onUpdate={updateBlock} />}
                        {selectedBlock.type === 'summary' && <SummaryProperties block={selectedBlock} onUpdate={updateBlock} allBlocks={blocks} />}
                        {selectedBlock.type === 'agenda' && <AgendaProperties block={selectedBlock} onUpdate={updateBlock} groups={seminar.groups || []} seminarDates={seminarDates} defaultDate={seminar.startDate}/>}
                        
                        <div className="pt-4 border-t">
                            <label className="block text-sm font-medium">Couleur de fond</label>
                            <input type="color" value={selectedBlock.backgroundColor} onChange={(e) => updateBlock(selectedBlock.id, { backgroundColor: e.target.value })} className="w-full h-8 mt-1" />
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">Sélectionnez un bloc pour le modifier.</p>
                )}
            </div>
        </div>
    );
};

export default DesignEditor;